export function galleryImagesFromHTML(html, origin) {
  const manifest = html.match(/<script\b[^>]*\bdata-gallery-images(?:\s|>)[\s\S]*?<\/script>/i);
  if (!manifest) return [];
  const items = JSON.parse(manifest[0].slice(manifest[0].indexOf('>') + 1).replace(/<\/script>$/i, ''));
  if (!Array.isArray(items)) return [];
  return items.filter(item => {
    if (!item || typeof item.src !== 'string' || typeof item.srcset !== 'string') return false;
    try {
      const url = new URL(item.src, origin);
      return url.protocol === 'https:' && (url.origin === origin || url.hostname === 'cdn.shopify.com');
    } catch { return false; }
  });
}

export function selectedGalleryImages(items, selection) {
  if (!Array.isArray(selection)) return [];
  const seen = new Set();
  return selection.flatMap(choice => {
    const item = items.find(item => item.id && String(item.id) === String(choice.id));
    if (!item || seen.has(item.id)) return [];
    seen.add(item.id);
    return [{ ...item, ...choice }];
  });
}

export function editorialFocus(focus, fallback) {
  return { center: '50% 50%', top: '50% 0%', bottom: '50% 100%', left: '0% 50%', right: '100% 50%' }[focus] || fallback || '50% 50%';
}

class EditorialRotation extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.revision = (this.revision || 0) + 1;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = !('IntersectionObserver' in window);
    this.current = -1;
    this.items = null;
    this.prepared = null;
    this.loading = false;
    this.selection = [];
    try { this.selection = JSON.parse(this.querySelector('[data-home-selection]')?.textContent || '[]'); } catch {}
    if (!Array.isArray(this.selection)) this.selection = [];
    this.interval = Math.max(5, Math.min(12, Number(this.dataset.interval) || 6)) * 1000;
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.reduced.addEventListener('change', () => {
      if (this.reduced.matches) this.motion?.finish();
      this.sync();
    }, { signal });
    this.addEventListener('shopify:block:select', async event => {
      const id = event.detail?.blockId;
      this.stop();
      if (!this.items) await this.load();
      const index = this.items?.findIndex(item => item.blockId === id);
      if (index >= 0) await this.show(index);
    }, { signal });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      }, { threshold: .1 });
      this.observer.observe(this);
    }
    this.sync();
  }

  canRotate() {
    return this.isConnected && this.visible && !document.hidden && !this.reduced.matches && !window.Shopify?.designMode;
  }

  sync() {
    this.stop();
    if (!this.visible || document.hidden || this.loading || !this.dataset.galleryUrl || !this.selection.length) return;
    if (this.reduced.matches && this.querySelector('img')) return;
    if (!this.items) this.load();
    else if (this.items.length && this.current < 0 && (this.canRotate() || !this.querySelector('img'))) this.show(0);
    else if (this.items.length > 1 && this.canRotate()) {
      this.prepareNext();
      this.timer = setTimeout(() => this.advance(), this.interval);
    }
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    const revision = this.revision;
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 8000);
    try {
      const url = new URL(this.dataset.galleryUrl, location.origin);
      if (url.origin !== location.origin) { this.items = []; return; }
      const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Gallery unavailable');
      const items = selectedGalleryImages(galleryImagesFromHTML(await response.text(), location.origin), this.selection);
      if (revision !== this.revision) return;
      this.items = items;
    } catch {
      if (revision === this.revision) this.items = [];
    } finally {
      clearTimeout(timeout);
      if (revision === this.revision) { this.loading = false; this.sync(); }
    }
  }

  async imageAt(index) {
    const item = this.items[index];
    const image = new Image(item.width, item.height);
    image.alt = item.alt || '';
    image.decoding = 'async';
    image.sizes = this.dataset.sizes;
    const focus = item.focus === 'inherit' ? this.dataset.focus : item.focus;
    const desktop = editorialFocus(focus, item.focal);
    const mobile = item.mobileFocus === 'inherit' ? this.dataset.mobileFocus : item.mobileFocus;
    image.style.setProperty('--editorial-image-focal', desktop);
    image.style.setProperty('--editorial-mobile-focal', !mobile || ['inherit', 'desktop'].includes(mobile) ? desktop : editorialFocus(mobile, item.focal));
    image.srcset = item.srcset;
    image.src = item.src;
    await image.decode();
    return image;
  }

  prepareNext() {
    if (this.prepared) return;
    const index = (this.current + 1) % this.items.length;
    this.prepared = { index, image: this.imageAt(index).catch(() => null) };
  }

  async show(index, prepared) {
    const mediaVersion = this.mediaVersion = (this.mediaVersion || 0) + 1;
    if (this.motion) {
      this.motion.finish();
      this.motion = null;
      const layers = [...this.querySelectorAll('img')];
      layers.slice(0, -1).forEach(image => image.remove());
    }
    this.loading = true;
    const revision = this.revision;
    const image = await (prepared || this.imageAt(index).catch(() => null));
    if (revision !== this.revision || mediaVersion !== this.mediaVersion || !this.isConnected) return;
    const previous = this.querySelector('img');
    if (!image) {
      this.items.splice(index, 1);
      if (index <= this.current) this.current--;
    } else if (previous && !this.canRotate() && !window.Shopify?.designMode) {
      this.loading = false;
      return;
    } else {
      this.append(image);
      this.current = index;
      // Keep the decoded previous layer underneath until the incoming layer is opaque.
      this.motion = previous && !this.reduced.matches ? image.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 800, easing: 'ease-in-out' }) : null;
      try { await this.motion?.finished; } catch {}
      if (revision !== this.revision || mediaVersion !== this.mediaVersion) return;
      previous?.remove();
      this.motion = null;
    }
    this.loading = false;
    this.sync();
  }

  async advance() {
    this.timer = null;
    if (!this.canRotate() || this.loading) return;
    this.prepareNext();
    const prepared = this.prepared;
    this.prepared = null;
    await this.show(prepared.index, prepared.image);
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  disconnectedCallback() {
    this.revision = (this.revision || 0) + 1;
    this.stop();
    this.motion?.finish();
    this.motion = null;
    this.querySelectorAll?.('img').forEach((image, index) => { if (index) image.remove(); });
    this.controller?.abort();
    this.request?.abort();
    this.observer?.disconnect();
    this.prepared = null;
  }
}

if (!customElements.get('editorial-rotation')) customElements.define('editorial-rotation', EditorialRotation);
